// src/features/sessions/lib/invalidateSessionCaches.ts
import type { QueryClient } from '@tanstack/react-query';

import { clientsKeys } from '@/features/clients/hooks';
import { engagementResourcesKeys } from '@/features/engagement-resources/hooks';
import { clientExercisesKeys } from '@/features/exercises/hooks';
import { sessionsKeys } from '@/features/sessions/hooks';

/**
 * Invalidates the queries that compose a client's timeline plus the rail's
 * sessions list. Used by every mutation that affects either the client's
 * timeline or the rail's sort order.
 */
export function invalidateClientTimeline(qc: QueryClient, clientId: string): void {
  void qc.invalidateQueries({ queryKey: sessionsKeys.lists() });
  void qc.invalidateQueries({ queryKey: clientsKeys.operation('red-flags', clientId) });
  void qc.invalidateQueries({ queryKey: clientsKeys.operation('assessments', clientId) });
  // client-exercises lives under its own makeQueryKeys('client-exercises') namespace;
  // prefix-invalidate the by-client operation across all params combos.
  void qc.invalidateQueries({ queryKey: clientExercisesKeys.operation('by-client') });
  void qc.invalidateQueries({
    queryKey: engagementResourcesKeys.operation('client-summary', { clientId }),
  });
}

/**
 * Invalidates the post-session clinical bundle for a session across every
 * view-mode variant. The bundle is what the SessionPanel drawer tabs render
 * (note + case-report cards), and its query key embeds the view-mode in the
 * operation string — `clinical-bundle:` (workspace) and
 * `clinical-bundle:client-history` (embedded History drawer). See
 * useClinicalBundle.ts. Note/case-report create/update/submit mutations must
 * call this so the drawer reflects the change without a manual page reload —
 * plain key invalidation of `note` / `case-reports` does NOT reach it.
 */
export function invalidateClinicalBundle(qc: QueryClient, sessionId: string): void {
  void qc.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'sessions' &&
        typeof key[1] === 'string' &&
        key[1].startsWith('clinical-bundle:') &&
        key[2] === sessionId
      );
    },
  });
}
